import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Context for managing page header state at the layout level
 * This allows pages to set their header props (title, count, actions, etc.)
 * while the header is rendered consistently in AdminLayout
 */

const PageHeaderContext = createContext(null);

// Default header state - used for reset and initial state
const defaultHeaderState = {
  title: '',
  icon: null,
  count: undefined,
  countIcon: null,
  loading: false,
  loadingType: 'spinner',
  actions: null,
  animate: true,
  enableNotificationPolling: false,
  style: {},
  breadcrumbs: null, // Array of { label: string, path?: string }
  customBreadcrumbLabel: null // Custom label for auto-generated breadcrumbs
};

export const PageHeaderProvider = ({ children }) => {
  const [headerProps, setHeaderProps] = useState(defaultHeaderState);

  // setPageHeader now fully replaces state (with defaults) instead of merging
  // This ensures old props from previous pages don't persist
  const setPageHeader = useCallback((props) => {
    setHeaderProps({
      ...defaultHeaderState,
      ...props
    });
  }, []);

  const resetPageHeader = useCallback(() => {
    setHeaderProps(defaultHeaderState);
  }, []);

  return (
    <PageHeaderContext.Provider value={{ headerProps, setPageHeader, resetPageHeader }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

/**
 * Hook to set page header props from any page component
 * 
 * Usage:
 * const { setPageHeader } = usePageHeader();
 * 
 * useEffect(() => {
 *   setPageHeader({
 *     title: 'Dashboard',
 *     count: items.length,
 *     loading: isLoading
 *   });
 * }, [items, isLoading]);
 */
export const usePageHeader = () => {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider');
  }
  return context;
};

export default PageHeaderContext;
